import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Unplug, Copy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const sel = "w-full px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm text-white focus:outline-none focus:border-[hsl(var(--primary))]";

function Setup({ status }) {
  const copy = () => navigator.clipboard.writeText(status.redirect_uri).then(() => toast.success("Copied"));
  return (
    <details className="mt-4 text-sm text-white/60" data-testid="google-setup-help">
      <summary className="cursor-pointer text-white/80 font-semibold">One-time Google Cloud setup</summary>
      <ol className="list-decimal pl-5 mt-2 space-y-1.5">
        <li>Open Google Cloud Console → the project of your Google login client.</li>
        <li>APIs &amp; Services → Library: enable <b>Google Search Console API</b>, <b>Google Analytics Data API</b> and <b>Google Analytics Admin API</b>.</li>
        <li>OAuth consent screen → Data access: add scopes <code>webmasters.readonly</code> and <code>analytics.readonly</code>. If the app is in Testing, add your Google email as a test user.</li>
        <li>Credentials → your OAuth Web client → Authorized redirect URIs → add:
          <div className="mt-1 flex items-center gap-2"><code className="px-2 py-1 rounded bg-black/40 break-all" data-testid="google-redirect-uri">{status.redirect_uri}</code>
            <button onClick={copy} className="text-[hsl(var(--primary))]" data-testid="google-copy-redirect"><Copy size={14} /></button></div></li>
        <li>Click <b>Connect Google</b> and sign in with the account that owns Search Console and GA4.</li>
      </ol>
    </details>
  );
}

export default function GoogleConnect({ status, onChange }) {
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState(null);
  const [cfg, setCfg] = useState({ site_url: status.site_url, property_id: status.property_id });

  useEffect(() => {
    if (status.connected) api.get("/admin/google/options").then((r) => setOpts(r.data)).catch((e) => { setOpts({ sites: [], properties: [] }); toast.error(formatApiError(e.response?.data?.detail)); });
  }, [status.connected]);

  const connect = async () => {
    setBusy(true);
    try { window.location.href = (await api.post("/admin/google/connect")).data.authorize_url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google? Dashboard data will stop loading.")) return;
    await api.delete("/admin/google"); onChange();
  };
  const save = async (next) => {
    setCfg(next);
    try { await api.put("/admin/google/config", next); onChange(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!status.connected) {
    return (
      <div className="card-surface rounded-xl p-6" data-testid="google-connect-card">
        <h3 className="font-bold text-white text-lg">Connect Google</h3>
        <p className="text-sm text-white/55 mt-1">Show real data from Google Search Console (clicks, impressions, CTR, position) and Google Analytics 4 (visitors per article, sources, realtime).</p>
        {!status.configured && <p className="mt-3 text-sm text-amber-400">GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET are missing on the server.</p>}
        <button onClick={connect} disabled={busy || !status.configured} data-testid="google-connect-btn"
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black font-bold text-sm disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <span className="font-black text-[#4285F4]">G</span>} Connect Google
        </button>
        <Setup status={status} />
      </div>
    );
  }
  return (
    <div className="card-surface rounded-xl p-5" data-testid="google-connected-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-white/70">Connected as <b className="text-white" data-testid="google-email">{status.email || "Google account"}</b></div>
        <button onClick={disconnect} data-testid="google-disconnect-btn" className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-red-400"><Unplug size={13} /> Disconnect</button>
      </div>
      <div className="grid md:grid-cols-2 gap-3 mt-4">
        <label className="text-xs font-bold uppercase tracking-wider text-white/50">Search Console property
          <select value={cfg.site_url} onChange={(e) => save({ ...cfg, site_url: e.target.value })} className={`${sel} mt-1.5`} data-testid="google-site-select" disabled={!opts}>
            <option value="">{opts ? "Choose property…" : "Loading…"}</option>
            {opts?.sites.map((s) => <option key={s} value={s}>{s}</option>)}
          </select></label>
        <label className="text-xs font-bold uppercase tracking-wider text-white/50">Google Analytics 4 property
          <select value={cfg.property_id} onChange={(e) => save({ ...cfg, property_id: e.target.value })} className={`${sel} mt-1.5`} data-testid="google-property-select" disabled={!opts}>
            <option value="">{opts ? "Choose property…" : "Loading…"}</option>
            {opts?.properties.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.account} ({p.id})</option>)}
          </select></label>
      </div>
    </div>
  );
}
